import React, { useState } from "react";
import { Search, Database, AlertCircle } from "lucide-react";
import SearchBar from "../common/SearchBar";
import { PrimaryButton } from "../common/ActionButtons";
import n8nConfig from "../../config/n8n.config";
import { AUTH_URL } from "../../constants";

const getAuthHeaders = () => {
  const token = localStorage.getItem("hyveai_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const QueryPinecone = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [queryVector, setQueryVector] = useState("");
  const [metadataFilters, setMetadataFilters] = useState({
    namespace: "",
    category: "",
    date_from: "",
    date_to: "",
    custom_field: "",
    custom_value: "",
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [actualDimensions, setActualDimensions] = useState(null);
  const [loadingDimensions, setLoadingDimensions] = useState(true);

  // Load stats on mount
  React.useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoadingDimensions(true);
    try {
      const response = await fetch(`${AUTH_URL}/pinecone/namespace-stats`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          index: n8nConfig.vector.indexName,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
        // Get actual dimensions from the index (more reliable than config)
        if (data.dimension) {
          setActualDimensions(data.dimension);
          console.log(`[QueryPinecone] Index dimensions: ${data.dimension}`);
        } else {
          // Fallback to config
          setActualDimensions(n8nConfig.vector.dimensions || 1024);
        }
      } else {
        // Fallback to config if stats fail
        setActualDimensions(n8nConfig.vector.dimensions || 1024);
      }
    } catch (error) {
      console.error("Error loading stats:", error);
      // Fallback to config
      setActualDimensions(n8nConfig.vector.dimensions || 1024);
    } finally {
      setLoadingDimensions(false);
    }
  };

  const handleQuery = async () => {
    setLoading(true);
    setError(null);

    try {
      // Check if dimensions are loaded
      if (loadingDimensions) {
        throw new Error("Loading index dimensions, please wait...");
      }

      // Parse query vector (comma-separated numbers) - optional now
      let parsedVector = null;
      if (queryVector.trim()) {
        parsedVector = queryVector.split(",").map((v) => parseFloat(v.trim()));

        // Validate dimensions (use actual index dimensions)
        if (parsedVector.length !== actualDimensions) {
          throw new Error(
            `Vector must have ${actualDimensions} dimensions (got ${parsedVector.length})`,
          );
        }
      }

      // Build metadata filter
      const filter = {};
      if (metadataFilters.category) filter.category = metadataFilters.category;
      if (metadataFilters.custom_field && metadataFilters.custom_value) {
        filter[metadataFilters.custom_field] = metadataFilters.custom_value;
      }

      // Check if we have either a vector OR metadata filters
      const hasFilters = Object.keys(filter).length > 0;
      if (!parsedVector && !hasFilters) {
        throw new Error(
          "Please provide either a query vector or metadata filters (category or custom field)",
        );
      }

      // If no vector, use zero vector for metadata-only query
      const queryVectorToUse = parsedVector || Array(actualDimensions).fill(0);

      // Query Pinecone
      const response = await fetch(`${AUTH_URL}/pinecone/query`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          index: n8nConfig.vector.indexName,
          namespace: metadataFilters.namespace || n8nConfig.vector.namespace,
          vector: queryVectorToUse,
          topK: n8nConfig.vector.topK || 100,
          filter: hasFilters ? filter : undefined,
          includeMetadata: true,
          includeValues: false,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Query failed");
      }

      const data = await response.json();
      setResults(data.matches || []);

      if (data.matches && data.matches.length > 0) {
        setError(null);
      } else {
        setError("No results found. Try different query parameters.");
      }
    } catch (error) {
      console.error("Error querying Pinecone:", error);
      setError(error.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">
          ℹ️ How to Query Pinecone
        </h3>
        <div className="text-sm text-blue-800 space-y-1">
          {loadingDimensions ? (
            <p className="text-blue-900 font-medium">
              ⏳ Loading index dimensions...
            </p>
          ) : actualDimensions ? (
            <>
              <p>
                <strong>Option 1:</strong> Vector similarity search - Enter{" "}
                {actualDimensions} comma-separated numbers
              </p>
              <p>
                <strong>Option 2:</strong> Metadata-only query - Leave vector
                empty, use filters below (category, custom fields)
              </p>
              <p>
                <strong>Option 3:</strong> Combined - Use both vector and
                filters for filtered similarity search
              </p>
              {stats && (
                <p className="mt-2 font-medium">
                  📊 <strong>Index:</strong>{" "}
                  {stats.totalVectorCount?.toLocaleString()} vectors,{" "}
                  {stats.dimension} dimensions
                </p>
              )}
            </>
          ) : (
            <p className="text-red-800">⚠️ Could not load index dimensions</p>
          )}
        </div>
      </div>

      {/* Query Input */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-slate-200">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">
          Query Pinecone Vector Database
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Query Vector{" "}
              <span className="text-slate-500">
                (optional - comma-separated)
              </span>
            </label>
            <textarea
              value={queryVector}
              onChange={(e) => setQueryVector(e.target.value)}
              placeholder={`0.1, 0.2, 0.3, ... (must be ${actualDimensions} comma-separated numbers)`}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Search Query{" "}
              <span className="text-slate-500">
                (text - requires embedding service)
              </span>
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter your search query..."
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-slate-700 mb-3">
              Metadata Filters
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Namespace
                </label>
                <input
                  type="text"
                  value={metadataFilters.namespace}
                  onChange={(e) =>
                    setMetadataFilters({
                      ...metadataFilters,
                      namespace: e.target.value,
                    })
                  }
                  placeholder={n8nConfig.vector.namespace || "default"}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Category
                </label>
                <input
                  type="text"
                  value={metadataFilters.category}
                  onChange={(e) =>
                    setMetadataFilters({
                      ...metadataFilters,
                      category: e.target.value,
                    })
                  }
                  placeholder="documents, images, etc."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Custom Field
                </label>
                <input
                  type="text"
                  value={metadataFilters.custom_field}
                  onChange={(e) =>
                    setMetadataFilters({
                      ...metadataFilters,
                      custom_field: e.target.value,
                    })
                  }
                  placeholder="e.g., author, source"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Custom Value
                </label>
                <input
                  type="text"
                  value={metadataFilters.custom_value}
                  onChange={(e) =>
                    setMetadataFilters({
                      ...metadataFilters,
                      custom_value: e.target.value,
                    })
                  }
                  placeholder="Field value"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <PrimaryButton
              onClick={handleQuery}
              icon={Search}
              disabled={loading}
            >
              {loading ? "Querying..." : "Search Vectors"}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-900">Query Error</h3>
            <p className="text-sm text-red-800 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">
            Query Results
          </h3>
          <p className="text-sm text-slate-600">
            {results.length > 0
              ? `Found ${results.length} matches`
              : "Results based on vector similarity and metadata filters"}
          </p>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-slate-600 mt-4">
                Searching vector database...
              </p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12">
              <Database className="w-16 h-16 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-800 mb-2">
                No Results
              </h3>
              <p className="text-slate-600">
                Enter a query vector above to search the database
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {results.map((result, index) => (
                <div
                  key={index}
                  className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-slate-900">{result.id}</h4>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      Score: {result.score?.toFixed(4)}
                    </span>
                  </div>
                  {result.metadata &&
                    Object.keys(result.metadata).length > 0 && (
                      <div className="text-sm text-slate-600 space-y-1 mt-2">
                        {Object.entries(result.metadata).map(([key, value]) => (
                          <div key={key} className="flex">
                            <span className="font-medium min-w-[100px]">
                              {key}:
                            </span>
                            <span className="flex-1">
                              {typeof value === "object"
                                ? JSON.stringify(value)
                                : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueryPinecone;
